"use client";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useStorage } from "@/lib/storage/provider";
import type { DocSummary } from "@/lib/storage/types";

export default function FilesSidebar({
  open,
  onOpenChange,
  onOpenDoc,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onOpenDoc: (id: string) => void;
}) {
  const { adapter } = useStorage();
  const [docs, setDocs] = useState<DocSummary[]>([]);
  const [q, setQ] = useState("");
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      const list = await adapter.list();
      if (!mounted) return;
      setDocs(list);
    })();
    return () => {
      mounted = false;
    };
  }, [open, adapter]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return docs;
    return docs.filter((d) => d.title.toLowerCase().includes(t));
  }, [docs, q]);

  const content = (
    <div className="fixed inset-y-0 left-0 z-[60] w-0 pointer-events-none">
      <div
        className={
          "relative h-full w-80 transform bg-white border-r shadow-xl transition-transform will-change-transform " +
          (open ? "translate-x-0 pointer-events-auto" : "-translate-x-full")
        }
      >
        <div className="flex h-12 items-center justify-between border-b px-3">
          <div className="font-medium">Files</div>
          <button
            className="rounded-md border px-2 py-1 text-sm hover:bg-accent"
            onClick={() => onOpenChange(false)}
          >
            Close
          </button>
        </div>
      <div className="p-3">
        <input
          placeholder="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-md border px-2 py-1 text-sm"
        />
        <button
          className="mt-3 w-full rounded-md bg-purple-600 px-3 py-1.5 text-white text-sm hover:bg-purple-700"
          onClick={async () => {
            const d = await adapter.create({ title: "Untitled" });
            onOpenDoc(d.id);
            onOpenChange(false);
          }}
        >
          New document
        </button>
      </div>
      <div className="mt-2 divide-y overflow-auto">
        {filtered.map((d) => (
          <div key={d.id} className="flex items-center gap-2 p-3">
            <button
              className="grow text-left hover:underline"
              onClick={() => {
                onOpenDoc(d.id);
                onOpenChange(false);
              }}
            >
              <div className="truncate font-medium">{d.title || "Untitled"}</div>
              <div className="text-xs text-muted-foreground truncate">
                Updated {new Date(d.updatedAt).toLocaleString()}
              </div>
            </button>
            <button
              className="rounded-md border px-2 py-1 text-xs hover:bg-accent"
              onClick={async () => {
                await adapter.remove(d.id);
                setDocs(await adapter.list());
              }}
            >
              Delete
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="p-3 text-sm text-muted-foreground">No results</div>
        )}
      </div>
      </div>
    </div>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
